import { Route, SmartRouter } from '@pancakeswap/smart-router/evm'
import { useTranslation } from '@pancakeswap/localization'
import { AutoColumn, Modal, ModalV2, UseModalV2Props, useTooltip } from '@pancakeswap/uikit'
import { Currency } from '@pancakeswap/sdk'
import { memo, useMemo } from 'react'
import { RouterBox, RouterTypeText } from '@/views/Swap/components/RouterViewer'
import { CurrencyLogo } from '@/components/currency-logo'
import { v3FeeToPercent } from '../utils/exchange'
import { RouteModelTooltip } from '@/components/tooltips/route-model'
import { TooltipWithTrigger } from '@/components/tooltips/base'
import { cn } from '@/lib/utils'

type Pair = [Currency, Currency]

interface Props extends UseModalV2Props {
  routes: Route[]
}

export const RouteDisplayModal = memo(function RouteDisplayModal({ isOpen, onDismiss, routes }: Props) {
  const { t } = useTranslation()
  return (
    <ModalV2 closeOnOverlayClick isOpen={isOpen} onDismiss={onDismiss} minHeight="0">
      <Modal
        title={
          <div className={'flex items-center gap-1 justify-center'}>
            {t('Route')} <RouteModelTooltip />
          </div>
        }
        style={{ minHeight: '0' }}
        bodyPadding="24px"
      >
        <AutoColumn gap="48px">
          {routes.map((route, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <RouteDisplay key={i} route={route} />
          ))}
          {/* <RoutingSettingsButton /> */}
        </AutoColumn>
      </Modal>
    </ModalV2>
  )
})

interface RouteDisplayProps {
  route: Route
}

export const RouteDisplay = memo(function RouteDisplay({ route }: RouteDisplayProps) {
  const { t } = useTranslation()
  const { path, pools, inputAmount, outputAmount } = route
  const { currency: inputCurrency } = inputAmount
  const { currency: outputCurrency } = outputAmount

  const pairs = useMemo<Pair[]>(() => {
    if (path.length <= 1) {
      return []
    }

    const currencyPairs: Pair[] = []
    for (let i = 0; i < path.length - 1; i += 1) {
      currencyPairs.push([path[i], path[i + 1]])
    }
    return currencyPairs
  }, [path])

  const pairNodes =
    pairs.length > 0
      ? pairs.map((p, index) => {
          const [input, output] = p
          const pool = pools[index]
          const isV3Pool = SmartRouter.isV3Pool(pool)
          const isV2Pool = SmartRouter.isV2Pool(pool)
          const key = isV2Pool ? `v2_${pool.reserve0.currency.symbol}_${pool.reserve1.currency.symbol}` : pool.address
          const text = isV2Pool
            ? 'V2'
            : isV3Pool
            ? `V3 (${v3FeeToPercent(pool.fee).toSignificant(6)}%)`
            : t('StableSwap')
          const tooltipText = `${input.symbol}/${output.symbol}${
            isV3Pool ? ` (${v3FeeToPercent(pool.fee).toSignificant(6)}%)` : ''
          }`

          return (
            <TooltipWithTrigger
              trigger={
                <div
                  className={cn('flex relative z-10 p-1 rounded-full', {
                    'bg-primary text-primary-foreground': isV3Pool,
                    'bg-muted': isV2Pool,
                  })}
                >
                  <CurrencyLogo size={32} chainId={input.chainId} address={input.wrapped.address} />
                  <CurrencyLogo size={32} chainId={output.chainId} address={output.wrapped.address} />
                  <RouterTypeText>{text}</RouterTypeText>
                </div>
              }
            >
              {tooltipText}
            </TooltipWithTrigger>
          )
        })
      : null

  return (
    <AutoColumn gap="24px">
      <RouterBox justifyContent="space-between" alignItems="center">
        <TooltipWithTrigger
          trigger={
            <div className={'relative z-10'}>
              <CurrencyLogo size={32} chainId={inputCurrency.chainId} address={inputCurrency.wrapped.address} />
              <RouterTypeText fontWeight="bold">{route.percent}%</RouterTypeText>
            </div>
          }
        >
          {inputCurrency.symbol}
        </TooltipWithTrigger>
        {pairNodes}
        <TooltipWithTrigger
          trigger={
            <div className={'relative z-10'}>
              <CurrencyLogo size={32} chainId={inputCurrency.chainId} address={outputCurrency.wrapped.address} />
            </div>
          }
        >
          {outputCurrency.symbol}
        </TooltipWithTrigger>
      </RouterBox>
    </AutoColumn>
  )
})

function PairNode({
  pair,
  text,
  className,
  tooltipText,
}: {
  pair: Pair
  text: string
  className: string
  tooltipText: string
}) {
  const [input, output] = pair

  const tooltip = useTooltip(tooltipText)

  return (
    <div className={cn(className, 'flex relative z-10 bg-muted p-1 rounded-full')} ref={tooltip.targetRef}>
      {tooltip.tooltipVisible && tooltip.tooltip}
      <CurrencyLogo chainId={input.chainId} address={input.wrapped.address} size={32} />
      <CurrencyLogo chainId={output.chainId} address={output.wrapped.address} size={32} />
      <RouterTypeText>{text}</RouterTypeText>
    </div>
  )
}
